// pms-core / ports / driven — REPOSITORY & QUERY ports (the "SPI").
//
// These are the persistence seams the application depends on and the Supabase
// adapter implements. Key boundary rule (docs/PMS-P2-ARCHITECTURE-BRIEF.md §3):
// the transactional INVARIANTS live in the PL/pgSQL RPCs, so the write methods
// here are thin fronts over those RPCs (one call = one atomic tx). The
// repository does NOT re-implement the state machine — it delegates. Reads map
// snake_case rows → domain types.

import type { Result } from '../../domain/result.js';
import type { TillCarte, TillProductDraft } from '../../domain/tillCarte.js';
import type { PublicOfferFacts } from '../../domain/directQuote.js';
import type { DeskOfferFacts } from '../../domain/deskOffer.js';
import type { Turnaway, TurnawayInput } from '../../domain/turnaway.js';
import type { ConfirmationMail, ConfirmationMailDraft, ConfirmationMailSendResult } from '../../domain/confirmationMail.js';
import type { CancellationPolicy, StoredCancellationPolicy, TermsRules } from '../../domain/cancellationPolicy.js';
import type { BankTransferDetails } from '../../domain/arrhes.js';
import type { PmsPropertyMode } from '../../domain/bookingHolder.js';
import type { CancellationFeeLine, CancellationReason, StayTermsBase, TrainingResetReport } from '../../domain/cancelQuote.js';
import type {
  AvailabilityCell,
  BedConfig,
  BedType,
  BillingMode,
  Cents,
  ChannexOccupancyOption,
  ChannexRatePlanLink,
  Charge,
  ChargeDay,
  ChargeId,
  Folio,
  FolioId,
  OccupancySpan,
  OccupancySpanInput,
  StayPerson,
  StayPersonMeal,
  StayPersonNeeds,
  OtaService,
  Product,
  ProductDraft,
  ProductId,
  CustomerSubtype,
  Guest,
  GuestDraft,
  GuestId,
  GuestSearch,
  GuestSearchResult,
  GuestSheet,
  GuestSheetPatch,
  IsoDate,
  Reservation,
  ReservationEvent,
  ReservationId,
  ReservationView,
  Room,
  RoomBed,
  RoomBlock,
  RoomConfigPatch,
  NewRoom,
  PropertyCode,
  TenantCode,
  RackWindow,
  BookingSearchResult,
  RoomId,
  RoomStayId,
  RoomStayView,
  RoomType,
  RoomTypeId,
  Rate,
  RatePlan,
  RatePlanId,
  AssignRoomStayOptions,
} from '../../domain/types.js';
import type { RateParameterSet, StoredRateParameterSet, RateSeason } from '../../domain/rateParameters.js';
import type { CalendarPeriod, RateProfile } from '../../domain/gridGenerator.js';
import type { ProposedPeriod } from '../../domain/seasonProposal.js';
import type {
  CreateReservationInput,
  ReservationCommand,
  ReservationPatch,
  TransitionAction,
} from '../driving/services.js';
import type { InboundApplied, InboundBooking } from './integrations.js';

// ── Public offer — the facts a direct quote is priced from (A4) ──────────────

export interface PublicOfferRepository {
  /** pms_public_offer — one RPC, service role only. `to` is the departure
   *  (exclusive); the RPC refuses a window outside 1–30 nights. */
  offer(propertyCode: string, from: string, to: string): Promise<Result<PublicOfferFacts>>;
  /** pms_public_terms (PB7b) — each policy the root plans follow, with their codes. */
  terms(propertyCode: string): Promise<Result<{ code: string; plans: string[]; rules: TermsRules }[]>>;
}

/** pms_desk_offer (PB3) — the same facts for a signed-in desk, gated by
 *  `pms.reservation.write` and the établissement in reach, plus what `sellAs`
 *  needs: each category's standing and whether a full one may be lodged bigger. */
export interface DeskOfferRepository {
  offer(propertyCode: string, from: string, to: string): Promise<Result<DeskOfferFacts>>;
}

/** « Demandes sans suite » (PB9) — written by `pms_record_turnaway`, read by
 *  `pms_turnaways_month` (the month in Europe/Paris, RLS deciding what is seen). */
export interface TurnawayRepository {
  record(input: TurnawayInput): Promise<Result<number>>;
  month(propertyCode: string, month: IsoDate): Promise<Result<Turnaway[]>>;
}

/** The booking confirmation's outbox (M2): `mail_confirmation_prepare` /
 *  `_set_link`, and the table's rows under RLS (pms.reservation.read, in reach). */
export interface ConfirmationMailRepository {
  prepare(draft: ConfirmationMailDraft): Promise<Result<string>>;
  setLink(id: string, link: string): Promise<Result<void>>;
  latest(reservationId: string): Promise<Result<ConfirmationMail | null>>;
  /** Not yet sent (awaiting the link, queued, failed), oldest first. */
  pending(): Promise<Result<ConfirmationMail[]>>;
}

/** The send itself — `/api/mail/send` on the site project (the key never reaches a browser). */
export interface ConfirmationMailDispatch {
  send(id: string): Promise<Result<ConfirmationMailSendResult>>;
}

/** The command `pms_direct_booking` reads (A5). Codes, not ids: the door
 *  resolves them inside its transaction. `nights` are the SIGNED prices. */
export interface DirectBookingCommand {
  envelope: { source: 'direct'; externalRef: string; revision: string; propertyCode: string };
  guest: { lastName: string; firstName: string; email: string; phone: string; lang: string };
  booking: { message: string | null; arrivalFrom: string | null; arrivalTo: string | null };
  rooms: {
    categoryCode: string;
    planCode: string;
    checkin: string;
    checkout: string;
    adults: number;
    children: number;
    infants: number;
    occupantName: string | null;
    extras: { code: string; quantity: number }[];
    nights: { date: string; priceCents: number }[];
  }[];
}

export interface DirectBookingWritten {
  reservationId: string;
  ref: string;
  totalCents: number;
  depositCents: number;
  replay: boolean;
}

export interface DirectBookingRepository {
  /** pms_direct_booking — service role only. Refusals come back as errors
   *  whose kind names them: `forbidden` (not_open), `conflict`
   *  (idempotency_conflict), `unavailable` (full). */
  book(command: DirectBookingCommand): Promise<Result<DirectBookingWritten>>;
}

// ── Reservation repository — writes delegate to RPCs, atomically ─────────────

export interface ReservationRepository {
  /**
   * Which of these channel booking ids we ALREADY hold.
   *
   * Exists so the tier-2 sweep can tell « recovered » from « we had it all
   * along ». Without it the sweep would re-apply every booking in the window —
   * harmless, since the RPC is idempotent, but it would raise the operator
   * alert D3 requires on every booking every night, and an alert that fires
   * for normality is an alert nobody reads.
   */
  knownChannexBookingIds(ids: readonly string[]): Promise<Result<string[]>>;

  /** pms_create_reservation — explodes nights + writes the 'created' event.
   *  Takes a guest that ALREADY exists; the form flow wants `bookWithGuest`. */
  create(input: CreateReservationInput): Promise<Result<ReservationId>>;

  /** pms_update_reservation — voids old nights, appends new, 'modified' event.
   *  Same caveat as `create`: the form flow wants `saveWithGuest`. */
  update(id: ReservationId, patch: ReservationPatch): Promise<Result<ReservationId>>;

  /** pms_reservation_apply — the one write command (A1): guest upsert AND
   *  create or patch in ONE transaction.
   *
   *  Replaces `bookWithGuest` / `saveWithGuest` (pms_book_reservation /
   *  pms_save_reservation). The single-transaction reason those had is kept:
   *  composing `guests.upsert()` then `create()` in the service was two
   *  transactions, so a refused create left the guest committed (review R4 /
   *  F16). Both aggregates change together or not at all. */
  apply(command: ReservationCommand): Promise<Result<ReservationId>>;

  /** pms_assign_room — validates type match, writes room_assigned/unassigned.
   *
   *  REFUSES on a booking holding several logements (0090): assigning « the
   *  room » of a two-room booking is ambiguous, and the RPC says so rather
   *  than picking. Use `assignRoomStay` whenever a logement is in hand. */
  assignRoom(id: ReservationId, roomId: RoomId | null): Promise<Result<void>>;

  /** pms_assign_room_stay — assignment where it is unambiguous: ONE room. */
  assignRoomStay(id: RoomStayId, roomId: RoomId | null, opts?: AssignRoomStayOptions): Promise<Result<void>>;

  /** pms_reservation_transition — the BOOKING-level step, a delegating shim
   *  since 0091. Commercial acts (confirm/unconfirm/cancel) apply booking-wide;
   *  physical ones (checkin/checkout/…) are refused when the booking holds
   *  several logements. Prefer `transitionRoomStay` for those. */
  transition(
    id: ReservationId,
    action: TransitionAction,
    reason?: string | null,
    reasonCode?: string | null,
    fee?: readonly CancellationFeeLine[] | null,
  ): Promise<Result<Reservation['status']>>;

  /** The closed list of cancellation reasons, active ones, in the desk's order (0138). */
  cancellationReasons(): Promise<Result<CancellationReason[]>>;

  /** Per logement, what its cancellation terms apply to — never the taxe de
   *  séjour (`pms_room_stay_terms_base`, OB3). */
  termsBase(id: ReservationId): Promise<Result<StayTermsBase[]>>;

  /** `pms_training_reset` (OB5): purges a TRAINING établissement's bookings and
   *  test data; the database refuses any other établissement. */
  resetTraining(propertyCode: string): Promise<Result<TrainingResetReport>>;

  /** `pms_property_day.current_day` — null when the établissement has none. */
  currentDay(propertyCode: string): Promise<Result<IsoDate | null>>;

  /** `pms_close_day` — the passage de jour, by hand (OB6). Refused in proof, and
   *  while an arrival of the day is unresolved (C4). Resolves to the new day. */
  closeDay(propertyCode: string, expectedDay: IsoDate): Promise<Result<IsoDate>>;

  /** pms_room_stay_transition — the state machine itself (0091). A guest checks
   *  in to a ROOM, so this is the honest call for every physical act; it
   *  returns the LOGEMENT's new status, not the booking's derived one. */
  transitionRoomStay(
    id: RoomStayId,
    action: TransitionAction,
    reason?: string | null,
  ): Promise<Result<Reservation['status']>>;

  /** The booking's logements, checkin-ordered — RELEASED ONES INCLUDED.
   *
   *  Deliberately not filtered: a released room is what explains a booking that
   *  once held two, and its charges outlive it for the invoice. Callers that
   *  want the live set filter on `cancelledAt === null`, which is also what
   *  `pmsLiveRoomCount` does. */
  roomStays(id: ReservationId): Promise<Result<RoomStayView[]>>;

  /** EVERY live logement, checkin-ordered — what « Réservations » counts.
   *
   *  Released ones are excluded here, unlike `roomStays(id)`: a released room
   *  holds no inventory, so drawing its bar would show a unit as occupied that
   *  anyone may book. The per-booking read keeps them because the FORM has to
   *  explain a room that was let go; a grid has nothing to explain it with.
   *
   *  Unwindowed, and that stopped being affordable: PostgREST returns at most
   *  1 000 rows with no error, and the Vega seed never removes a checked-out
   *  stay (877 live at HOTEL on 2026-09-30, +16 a day). The rack moved to
   *  `rackWindow`; the list still reads this until its own server-side read is
   *  designed, so meanwhile it REFUSES rather than returns a truncated set —
   *  a list that silently ends reads as « no more bookings ». */
  listAllRoomStays(): Promise<Result<RoomStayView[]>>;

  /** The rack's read for [from, to) — see `RackWindow` for the four parts.
   *
   *  ONE value, not a table read, so PostgREST's row cap does not apply; the
   *  RPC bounds it itself and REFUSES beyond (a period over 400 days, more than
   *  5 000 logements) instead of truncating, because a rack missing stays
   *  draws their rooms free. A missing capability or an établissement out of
   *  reach is a denial, never an empty rack. */
  rackWindow(from: IsoDate, to: IsoDate): Promise<Result<RackWindow>>;

  /** The planning's search box: bookings whose Vega number, OTA reference,
   *  group, client (name or number) or a logement's holder contains EVERY word
   *  of `query`. At most `limit` (default 20, max 50) and `more` says when there
   *  were others. A missing capability is a denial, never « no booking found ». */
  searchBookings(query: string, limit?: number): Promise<Result<BookingSearchResult>>;

  findById(id: ReservationId): Promise<Result<ReservationView | null>>;

  /** Every reservation, checkin-ordered — « Réservations » only now. Refuses
   *  past PostgREST's 1 000 rows, for the reason `listAllRoomStays` gives. */
  listAll(): Promise<Result<ReservationView[]>>;

  /** Desk/list read over a date window (checkin ≤ to AND checkout ≥ from). */
  listOverlapping(from: IsoDate, to: IsoDate): Promise<Result<ReservationView[]>>;

  /** Every booking arriving in [from, to], cancelled ones included — the PMS
   *  side of « Pointage OTA » (CX2). Refuses rather than truncates. */
  listArriving(from: IsoDate, to: IsoDate): Promise<Result<ReservationView[]>>;

  /** Apply a decoded OTA booking atomically (pms_apply_inbound_booking):
   *  idempotent on channexBookingId, create/modify/cancel per booking.action.
   *  Returns null when a cancel arrives for a booking we never saw (§8). */
  applyInboundBooking(booking: InboundBooking): Promise<Result<InboundApplied | null>>;

  /** Idempotency lookup for the Channex webhook (P2). */
  findByChannexBookingId(channexBookingId: string): Promise<Result<Reservation | null>>;

  /** Append-only audit trail for one reservation (newest last). */
  events(id: ReservationId): Promise<Result<ReservationEvent[]>>;

  /** The stay's ACTIVE charge DAYS, date-ordered (`pms_charge_day`).
   *
   *  The EVIDENCE layer: what an invoice is built from and what a communal
   *  return is audited against. Read-only, and deliberately no write method
   *  beside it — a charge day is never updated in place (Hard Rule 5), and the
   *  ONE way they change is through the write path, which rewrites the set
   *  atomically. A `saveChargeDays` here would be a second door onto an
   *  invariant the RPC owns.
   *
   *  For anything a person READS or EDITS, use `charges` below instead. */
  chargeDays(id: ReservationId): Promise<Result<ChargeDay[]>>;

  /** The booking's ACTIVE agreed charges — the layer a folio PRINTS (0096).
   *
   *  This, not `chargeDays`, is what the folio and the charge grid read: it
   *  carries the range and the quantity a person typed, so every total is
   *  arithmetic over it. Pulling the day rows to re-derive a number these
   *  already state would be an audit trail used as a calculator. */
  charges(id: ReservationId): Promise<Result<Charge[]>>;

  /** The booking's folios — who pays (0098). */
  folios(id: ReservationId): Promise<Result<Folio[]>>;

  /** A logement's occupancy spans, in date order (0097). They tile its stay
   *  exactly, so no caller has to reason about a gap. */
  occupancy(id: RoomStayId): Promise<Result<OccupancySpan[]>>;

  /** The booking's persons (group edit step 1), room then position order. */
  persons(id: ReservationId): Promise<Result<StayPerson[]>>;

  /** The booking's meal exceptions (group edit step 2). */
  personMeals(id: ReservationId): Promise<Result<StayPersonMeal[]>>;

  /** The booking's needs and allergens (group edit step 5) — health data. Rows
   *  the caller may not read are FILTERED by the policy, not refused, so an
   *  empty answer is not « nothing declared »: gate on `pms.needs.read` first. */
  personNeeds(id: ReservationId): Promise<Result<StayPersonNeeds[]>>;

  /** What the channel sold with this booking's logements (0094) — shown on the
   *  folio as « vendu par l'OTA », never added to a total. */
  otaServices(id: ReservationId): Promise<Result<OtaService[]>>;

  /** Replaces a logement's occupancy with a COMPLETE, contiguous set covering
   *  its stay, then re-derives the taxe de séjour and per-person extras.
   *
   *  A write, unlike everything above, and it sits here rather than beside the
   *  reservation RPCs because it is what the levy is computed FROM: the desk
   *  edits who is present and the declaration follows, which is the whole
   *  reason the tax is not typeable. */
  writeOccupancy(id: RoomStayId, spans: readonly OccupancySpanInput[]): Promise<Result<Cents>>;

  /** Lays the booking's folios out again from one of the three configurations,
   *  re-routing every charge — including ones moved by hand. Deliberately
   *  destructive of previous moves: a desk that picks a configuration expects
   *  that configuration, not a merge with what was there. */
  setBillingMode(id: ReservationId, mode: BillingMode): Promise<Result<number>>;

  /** Moves charges between folios of the SAME booking — the split/consolidate
   *  the counter needs. */
  moveCharges(chargeIds: readonly ChargeId[], folioId: FolioId): Promise<Result<number>>;

  /** pms_reservation_mark_vad_done — vad_pending → vad_done (A5). */
  markVadDone(id: ReservationId): Promise<Result<void>>;
  /** pms_reservation_mark_transfer_received — transfer_pending → transfer_done. */
  markTransferReceived(id: ReservationId): Promise<Result<void>>;
}

// ── Guest repository ─────────────────────────────────────────────────────────

export interface GuestRepository {
  /** pms_upsert_guest — id=null inserts, else updates. */
  upsert(id: GuestId | null, draft: GuestDraft): Promise<Result<GuestId>>;
  findById(id: GuestId): Promise<Result<Guest | null>>;
  /** pms_guest_search — never the whole table: PostgREST stops at 1 000 rows
   *  and the cardex holds 5 343 (PMS-CARDEX-BRIEF §6.1 S2). Raises on a denial
   *  rather than returning « no such client ». */
  search(q: GuestSearch): Promise<Result<GuestSearchResult>>;
  /** pms_guest_sheet — the fiche client. */
  sheet(id: GuestId): Promise<Result<GuestSheet>>;
  /** pms_save_guest_sheet — only the keys present change (S4). */
  saveSheet(id: GuestId, patch: GuestSheetPatch): Promise<Result<void>>;
  /** The B2B sous-types, active and retired (a retired one still labels the
   *  clients that carry it). */
  listSubtypes(): Promise<Result<CustomerSubtype[]>>;
  /** Dedup HEURISTIC surface — candidates by email/phone. Never auto-merges. */
  findDuplicates(email: string | null, phone: string | null): Promise<Result<Guest[]>>;
}

// ── Room & block repository (reference data + P2 desk ops) ───────────────────

export interface RoomRepository {
  listRooms(): Promise<Result<Room[]>>;
  listRoomTypes(): Promise<Result<RoomType[]>>;

  /** P2 ONBOARDING — bind a room type to its Channex counterpart, or clear it.
   *
   *  `channex_room_type_id` has been READ by the Channex adapter since P2 and
   *  written by nothing: `pms_edit_room_type` excluded it deliberately (« the
   *  mapping belongs to P2 onboarding »), so an inbound booking for an unmapped
   *  category dead-letters as a NON-RETRYABLE error. This is that write path. */
  setChannexRoomTypeId(id: RoomTypeId, channexId: string | null): Promise<Result<void>>;
  /** The ARRANGEMENT vocabulary (`pms_bed_config`, brief E1 + 0173). Active
   *  codes only — a retired code stays readable through the rooms that use it. */
  listBedConfigs(): Promise<Result<BedConfig[]>>;

  /** The FURNITURE vocabulary (`pms_bed_type`, 0173). A separate call because
   *  they are separate facts: one call returning both would re-merge exactly
   *  what that migration split apart. */
  listBedTypes(): Promise<Result<BedType[]>>;

  /** States how a room is made up RIGHT NOW (`pms_set_room_current_config`).
   *
   *  ⚠ `source` has only two values, and `allocation` is not one of them
   *  (operator, 2026-09-16): a booking asking for a twin does not move a bed,
   *  so an assignment never writes this. `null` clears it back to « not
   *  known », which is a real statement and not a failure. */
  setRoomCurrentConfig(
    roomId: RoomId,
    bedConfig: string | null,
    source: 'menage' | 'desk',
  ): Promise<Result<void>>;

  /** Per-space beds for every MULTI-space unit in scope (`pms_room_bed`).
   *  Empty for a single-space property such as the hotel — the 42 rooms carry a
   *  scalar `bedConfig` instead, and the two are exclusive. */
  listRoomBeds(): Promise<Result<RoomBed[]>>;
  /** pms_create_room — a new physical room. Renumbering is deliberately absent
   *  (brief E5): the number is the identity reservations point at. */
  createRoom(draft: NewRoom): Promise<Result<RoomId>>;
  /** pms_delete_room — only a room NOTHING has ever referenced (brief E5). */
  deleteRoom(roomId: RoomId): Promise<Result<void>>;
  /** pms_update_room_config — the whole configuration row, atomically. Refuses
   *  a category change that would falsify a live assignment (brief O-D). */
  updateConfig(roomId: RoomId, patch: RoomConfigPatch): Promise<Result<void>>;
  /** P2 desk: set housekeeping status (RLS allows desk write). */
  setHkStatus(roomId: RoomId, status: Room['hkStatus']): Promise<Result<void>>;
  listBlocks(from: IsoDate, to: IsoDate): Promise<Result<RoomBlock[]>>;
  addBlock(block: Omit<RoomBlock, 'id'>): Promise<Result<number>>;
  removeBlock(blockId: number): Promise<Result<void>>;
}

// ── Rate repository (P1 read; P2 ARI grid is the push source) ────────────────

export interface RateRepository {
  listPlans(): Promise<Result<RatePlan[]>>;

  /** The (rate plan × room type) → Channex rate plan mapping (0102).
   *
   *  Per PAIR, not per plan: Channex requires a rate plan to belong to exactly
   *  one room type, so our one « Tarif standard » is six Channex plans at the
   *  hotel. `pushAri` reads this and would otherwise push five room types'
   *  restrictions onto whichever single plan the old column happened to name. */
  listChannexRatePlans(): Promise<Result<ChannexRatePlanLink[]>>;

  /** Bind one pair, or clear it (`null`).
   *
   *  The per-occupancy options travel WITH the binding rather than in a call of
   *  their own (0104): a plan bound without its options can be pushed to, and
   *  the push would reach the primary occupancy only while reporting success.
   *  One row, one write, no half-applied mapping (Hard Rule 2). */
  setChannexRatePlan(
    ratePlanId: RatePlanId,
    roomTypeId: RoomTypeId,
    channexId: string | null,
    occupancyOptions?: ChannexOccupancyOption[],
  ): Promise<Result<void>>;
  // ── Rate PARAMETER SETS (R14, migration 0107) ──────────────────────────
  //
  // On the rate repository for the same reason the product catalogue is: a
  // parameter set IS the tariff, one level up, and it is gated by the same
  // `pms.rate.*` capabilities. Whoever may not change a price may not change
  // the numbers every price is generated from.

  listParameterSets(): Promise<Result<StoredRateParameterSet[]>>;

  getParameterSet(id: number): Promise<Result<StoredRateParameterSet | null>>;

  /** Create a DRAFT. There is no « create published » on purpose: publishing is
   *  the act that freezes a set, and an object that could be born frozen would
   *  let a typo become permanent in one call. */
  createParameterSetDraft(draft: RateParameterSet, note: string | null): Promise<Result<number>>;

  /** Overwrite a draft's contents. Refused by the database for a published set
   *  — the guard lives there, not here, so no code path can skip it. */
  updateParameterSetDraft(id: number, draft: RateParameterSet, note: string | null): Promise<Result<void>>;

  /** draft → published. Irreversible: from here the set can only be archived. */
  publishParameterSet(id: number): Promise<Result<void>>;

  /** Retire a set from use without destroying it — the only exit a published
   *  set has. */
  archiveParameterSet(id: number): Promise<Result<void>>;

  /** Delete a DRAFT outright. Published and archived sets refuse, in the
   *  database. */
  deleteParameterSetDraft(id: number): Promise<Result<void>>;

  /** The établissement's ACTIVE product catalogue, tax tariffs included.
   *  On the RATE repository and not a repository of its own: a priced
   *  catalogue is rate data, and it is gated by pms.rate.* for the same
   *  reason — the desk that may not change a room's price may not invent a
   *  40 € pet supplement either. */
  listProducts(): Promise<Result<Product[]>>;
  /** Create or update a catalogue product (`pms_product`).
   *
   *  A DIRECT write, not an RPC, and deliberately: reference data in this
   *  system is RLS-audited rather than RPC-gated (D4 — the same shape
   *  `upsertRates` and the room blocks use). There is no cross-row invariant
   *  here for an RPC to own; every rule the catalogue has is a CHECK on the
   *  row, and the database refuses a bad one whatever writes it. */
  upsertProduct(draft: ProductDraft): Promise<Result<ProductId>>;

  /** Retire or restore a product. NEVER a delete: `pms_charge` names it, so a
   *  billed product cannot be removed, and a past folio must keep reading
   *  « Demi-pension » long after it stops being sold. */
  setProductActive(id: ProductId, active: boolean): Promise<Result<void>>;
  /** The ARI grid slice for [from, to): one room type, ALL the plans named.
   *
   *  Feeds the Channex ari_push (P2).
   *
   *  PLURAL, since 2026-08-23. It took one plan and `pushAri` called it in a
   *  loop, which made the push's HTTP cost a function of the catalogue — three
   *  round trips per job at one parent plus two channel children, and one more
   *  for every channel added afterwards. That is the same mistake `listRateGrid`
   *  below already names for the editor, arrived at from the other direction:
   *  there it cost load time, here it cost the invocation's subrequest budget
   *  and stranded two of six jobs (PMS-RATE-MODEL-BRIEF §24.1). */
  listRates(
    ratePlanIds: readonly RatePlanId[],
    roomTypeId: RoomTypeId,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<Rate[]>>;
  /** The WHOLE grid for a window, every plan × room type in scope.
   *
   *  Distinct from `listRates`, which reads ONE room type for the ARI push. The
   *  editor displays the grid as a whole, so reading it pair by pair would make
   *  the screen's load time a function of the catalogue size — six plans × six
   *  types is 36 round trips for one fortnight. */
  listRateGrid(from: IsoDate, to: IsoDate): Promise<Result<Rate[]>>;

  /** Store HAND edits — rows written here are marked `hand_adjusted` (0117),
   *  which the grid generator must never overwrite (R4). The generator will
   *  have its own write; do not route it through this one. */
  upsertRates(rates: Rate[]): Promise<Result<void>>;

  /**
   * Store a GENERATED grid — the other write, and the one that must never
   * destroy a hand edit.
   *
   * Backed by `pms_generate_rates` (0119) rather than an upsert: the guard is a
   * conditional `ON CONFLICT … WHERE NOT hand_adjusted` that PostgREST cannot
   * express, so done client-side it would be a read, a filter and a write with
   * a race in the middle. Returns what it wrote AND what it declined, because a
   * generator that silently skipped 34 days would leave the operator believing
   * the grid was refreshed.
   */
  generateRates(rates: Rate[]): Promise<Result<{ written: number; skipped: number }>>;
}

// ── Availability query — derived, read-only (wraps pms_availability_range) ───

export interface AvailabilityQuery {
  /** Derived availability per room type × day over [from, to].
   *
   *  `roomTypeId` NARROWS THE QUERY, it does not filter the answer. The ARI
   *  push needs one category and used to read all of them and discard the rest:
   *  at a 365-day horizon that is 2 190 rows fetched and parsed to use 365, six
   *  times over in a six-job tick (~13 000 rows for 2 190). Cloudflare Workers
   *  Free caps CPU at 10 ms per invocation and JSON parsing is charged to it,
   *  so « fetch it all and filter » is not free here — it is the one cost the
   *  subrequest budget of ADR-0017 does not count. */
  range(from: IsoDate, to: IsoDate, roomTypeId?: RoomTypeId): Promise<Result<AvailabilityCell[]>>;
  /** Available room ids of a type on a night — assignment picker + booking guard. */
  freeRooms(roomTypeId: RoomTypeId, checkin: IsoDate, checkout: IsoDate): Promise<Result<RoomId[]>>;
}

// ── Unit of work — the atomicity seam (Top-Level Hard Rule 2) ────────────────
//
// Most PMS writes are already atomic inside a single RPC, so the DEFAULT path
// needs no UoW. This port exists for the multi-step application flows that a
// single RPC can't cover — e.g. the Channex webhook that upserts a guest, then
// creates/modifies a reservation, then enqueues an ari_push, all-or-nothing.
// The Supabase adapter implements this via an RPC that wraps the steps, or via
// a transaction; the application never manages a raw connection.

export interface UnitOfWork {
  run<T>(work: (repos: RepositoryBundle) => Promise<Result<T>>): Promise<Result<T>>;
}

// ── Scope binding (P1.5 step 6) ──────────────────────────────────────────────
//
// A repository is CONSTRUCTED for exactly one scope. It is a constructor
// argument rather than a convention because of where the danger is: `webhook.ts`
// and `job-tick.ts` hold the service-role key and therefore bypass RLS
// ENTIRELY. Under one property that was harmless; under two it is the only code
// path with no database backstop (design brief §6.9, "the largest tenancy risk
// in the design"). Everywhere else in the system a mistake is caught by a
// policy or a composite foreign key. Here, nothing catches it — so the type has
// to, by making an unscoped query impossible to express rather than merely
// discouraged.

export type RepositoryScope =
  /** A browser session. Reads are narrowed by RLS to what the actor may see and
   *  writes resolve their établissement from the actor's sole property, so the
   *  adapter names no property and the database decides. */
  | { readonly kind: 'actor' }
  /** A service-role caller bound to ONE établissement. Every read filters on it
   *  and every write states it; RLS is not consulted and must not be relied on.
   *
   *  The tenant travels with it because the two scoping axes are not the same:
   *  reservations, rooms and rates are property-scoped, GUESTS are tenant-scoped
   *  (one person books the hotel in February and the apartments in August and is
   *  one guest — design brief §6.2). A property-only scope could not narrow a
   *  guest read correctly, and narrowing it by property would silently split the
   *  guest in two. Derived from the property via `app_property`, never passed
   *  independently, so the pair cannot disagree — the same rule the composite FK
   *  enforces in the database. */
  | {
      readonly kind: 'property';
      readonly propertyCode: PropertyCode;
      readonly tenantCode: TenantCode;
    }
  /** The job-claim loop, and NOTHING else. A single tick must drain every
   *  property's queue, so claiming is cross-property by nature — but reads and
   *  writes under this scope are REFUSED at runtime, which is what stops it
   *  becoming the unscoped mode the other two exist to prevent. */
  | { readonly kind: 'platform' };

/** Every scope a REPOSITORY may be built with. `platform` is excluded at the
 *  type level rather than checked at runtime: the job-claim loop is the only
 *  thing that legitimately spans properties, and making its scope unassignable
 *  to a repository is what turns "an unscoped query is not expressible" from a
 *  comment into something the compiler refuses. */
export type QueryScope = Exclude<RepositoryScope, { kind: 'platform' }>;

/**
 * DRIVEN PORT — how a repository reaches the scope it is bound to.
 *
 * Deliberately a SOURCE and not a value, for exactly the reason `ActorSource`
 * is (see `@lemontaigu/platform` capability.ts): the services are built ONCE at
 * the composition root, while in a browser the scope resolves asynchronously
 * and then CHANGES when the operator switches établissement. A composition root
 * that captured the scope by value at build time would pin every repository to
 * whatever was known at mount — which for a switcher means it silently does
 * nothing, and for the initial load means every read is scoped to the wrong
 * place or to nothing at all.
 *
 * SYNCHRONOUS for the same reason too: a repository method must narrow its
 * query without awaiting.
 *
 * FAILS SAFE, not closed: before the property list arrives, implementations
 * return `{ kind: 'actor' }` — "let the database decide". That is not a
 * loophole. RLS still narrows the read to the actor's grants and the RPCs still
 * resolve their établissement from `actor_default_property()`, which refuses
 * when ambiguous. The degraded state is exactly the behaviour that shipped
 * through P1.5, which is the safest thing an unresolved scope could mean.
 */
export interface ScopeSource {
  current(): QueryScope;
}

/** A scope that never changes — the service-role composition, where the
 *  property is a fact of the runtime and not of a user's choice. */
export function fixedScope(scope: QueryScope): ScopeSource {
  return { current: () => scope };
}

/** An établissement as the UI needs it: enough to switch between them and to
 *  label the choice. Read through RLS, so the list IS the actor's reach. */
export interface Property {
  code: PropertyCode;
  tenantCode: TenantCode;
  labelFr: string;
  kind: 'hotel' | 'apartments';
  /** `app_property.mode`: what decides whether a seeded booking is still
   *  Vega's (`bookingIsVegaHeld`). Optional so a caller that did not read it
   *  falls on the safe side — an unknown mode counts as not live. */
  mode?: PmsPropertyMode;
}

export interface PropertyRepository {
  /** Every établissement the caller may act in, from `app_property` under RLS.
   *  No capability gate of its own: the grant IS the authorization, and a
   *  principal who can see nothing gets an empty list rather than an error.
   *
   *  **Returned in presentation order**, and callers must not re-sort: the
   *  order is `app_property.sort_order` — a business ordering the operator
   *  controls — with `code` only as a tie-break. Sorting client-side would put
   *  the alphabet back in charge, and the alphabet puts APPART ahead of HOTEL
   *  (P1.6 build brief §3.3). `sortOrder` is deliberately NOT on `Property`
   *  for the same reason: a field nobody renders is an invitation to re-sort. */
  listForActor(): Promise<Result<Property[]>>;
}

/** The `property_code` a scope writes, or an error explaining why it has none.
 *  Callers must not paper over the error: a write with no établissement is the
 *  bug this whole binding exists to make impossible. */
export function scopedPropertyCode(scope: RepositoryScope): PropertyCode | null {
  return scope.kind === 'property' ? scope.propertyCode : null;
}

/** The repositories handed to a UnitOfWork body, all bound to the same tx. */
export interface RepositoryBundle {
  reservations: ReservationRepository;
  guests: GuestRepository;
  rooms: RoomRepository;
  rates: RateRepository;
  availability: AvailabilityQuery;
}

// ── The season calendar and the rate profile (step 6) ───────────────────────
//
// Two repositories rather than one, because they are two objects with two
// lifecycles — which is the entire point of the split. A calendar is LIVING
// (edited a period at a time, indefinitely); a profile is FROZEN once
// published. One repository holding both would be the welded document again,
// wearing an interface.

/** A calendar as stored: the header, its periods and its closures. */
export interface StoredSeasonCalendar {
  id: number;
  year: number;
  note: string | null;
  periods: CalendarPeriod[];
  closures: StoredClosure[];
}

export interface StoredClosure {
  id: number;
  from: IsoDate;
  to: IsoDate;
  reason: string;
}

/** What a period edit may change. Absent fields are left alone — a base tweak
 *  must not have to restate the dates it is not touching. */
export interface PeriodPatch {
  from?: IsoDate;
  to?: IsoDate;
  description?: string;
  zones?: string | null;
  season?: RateSeason;
  baseCents?: Cents;
}

export interface SeasonCalendarRepository {
  /** Every year on file, newest first. Small: one per fiscal year. */
  listCalendars(): Promise<Result<{ id: number; year: number; note: string | null }[]>>;

  /** One year, whole — header, periods and closures in a single call, because
   *  that is how the screen reads it and three round trips would show the
   *  calendar assembling itself. */
  getCalendar(id: number): Promise<Result<StoredSeasonCalendar | null>>;

  createCalendar(year: number, note: string | null): Promise<Result<number>>;
  deleteCalendar(id: number): Promise<Result<void>>;

  /** Bulk-insert proposed periods — what `proposePeriods` produces, after the
   *  operator has looked at it. */
  addPeriods(calendarId: number, periods: ProposedPeriod[]): Promise<Result<void>>;

  /** Change one period. A base change is logged by the DATABASE (0118), not
   *  here: a log the application maintains is a log the one forgotten path
   *  silently omits from. */
  updatePeriod(id: number, patch: PeriodPatch): Promise<Result<void>>;
  deletePeriod(id: number): Promise<Result<void>>;

  addClosure(calendarId: number, from: IsoDate, to: IsoDate, reason: string): Promise<Result<void>>;
  deleteClosure(id: number): Promise<Result<void>>;

  /** The base's history, newest first — what changed, when. */
  baseHistory(periodId: number): Promise<Result<BaseAdjustment[]>>;
}

export interface BaseAdjustment {
  fromCents: Cents;
  toCents: Cents;
  changedAt: string;
}

export interface StoredRateProfile {
  id: number;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  parameters: RateProfile;
}

export interface RateProfileRepository {
  listProfiles(): Promise<Result<StoredRateProfile[]>>;
  getProfile(id: number): Promise<Result<StoredRateProfile | null>>;
  createProfileDraft(draft: RateProfile, note: string | null): Promise<Result<number>>;
  updateProfileDraft(id: number, draft: RateProfile, note: string | null): Promise<Result<void>>;
  publishProfile(id: number): Promise<Result<void>>;
  archiveProfile(id: number): Promise<Result<void>>;
  deleteProfileDraft(id: number): Promise<Result<void>>;
}

/** The établissement's cancellation policies (PB7a, `pms_cancellation_policy`). DIRECT writes under RLS: the scale's
 *  shape, the percentages and the server-side « vérifié le » stamp are the table's own CHECKs and trigger. */
export interface CancellationPolicyRepository {
  listPolicies(): Promise<Result<StoredCancellationPolicy[]>>;
  /** Create (no id) or update. `verifiedAt` is never written here — see `verifyPolicy`. */
  savePolicy(draft: Omit<CancellationPolicy, 'verifiedAt'> & { id?: number }): Promise<Result<number>>;
  /** Refused by the database while a plan follows it. */
  deletePolicy(id: number): Promise<Result<void>>;
  /** « L'extranet affiche ces conditions » — the stamp is the server's clock. */
  verifyPolicy(id: number): Promise<Result<void>>;
  /** Which policy a rate plan is sold under; null unassigns. */
  assignPlanPolicy(ratePlanId: number, policyId: number | null): Promise<Result<void>>;
}

/** The till's carte (`pms_product` rows with a role, and the structure tables of T2). Scoped like every repository. */
export interface TillCatalogueRepository {
  loadCarte(): Promise<Result<TillCarte>>;
  /** A DIRECT write under RLS (D4, the extras catalogue's shape): every rule is a constraint or a trigger on the row. */
  upsertTillProduct(draft: TillProductDraft): Promise<Result<ProductId>>;
  setTillProductActive(id: ProductId, active: boolean): Promise<Result<void>>;
}

/** The property's bank-transfer instructions (`pms_property_bank`, one row per établissement). DIRECT under RLS: the
 *  IBAN's checksum, the BIC's shape and the delay are the table's own CHECKs. */
export interface BankTransferRepository {
  get(): Promise<Result<BankTransferDetails | null>>;
  /** Insert or update the current property's row. */
  save(details: Omit<BankTransferDetails, 'propertyCode'>): Promise<Result<void>>;
}
